import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IconWarn } from '../components/auth/AuthBits';
import { resendVerification, verifyEmail } from '../api';
import { getToken } from '../api/client';
import '../styles/page-login.css';
import Seo from '../components/Seo';

/* Two jobs on one URL, exactly like the host page:
     /verify-email?token=…  -> POST /auth/verify-email, which is what actually
                               flips isEmailVerified and activates billing.
     /verify-email          -> "check your inbox", with a resend button.

   Resending is NOT a public endpoint: /auth/resend-verification runs behind
   authMiddleware and reads req.user, so it only works for a signed-in user and
   ignores any email you pass it. The button is therefore only offered when a
   session token exists; otherwise we say plainly that signing in is required. */

const ACTIVATED = [
  'Your billing account goes live',
  'Every module unlocks for the trial',
  'You can invite your team',
  'Tracking and pattern reports start running',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* The address signup used, if we can find it — display only. */
function findEmail(fromQuery) {
  if (fromQuery && EMAIL_RE.test(fromQuery.trim())) return fromQuery.trim();
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u?.email && EMAIL_RE.test(String(u.email))) return String(u.email);
  } catch {
    /* unreadable storage is fine */
  }
  return '';
}

function Card({ children }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <img className="amark" src="/assets/mark-teamgrid.svg" alt="TeamGrid" />
        {children}
      </div>
    </div>
  );
}

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const known = useMemo(() => findEmail(params.get('email')), [params]);

  /* verifying | verified | failed | inbox */
  const [state, setState] = useState(token ? 'verifying' : 'inbox');
  const [failMsg, setFailMsg] = useState('');

  const [resent, setResent] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const signedIn = !!getToken();

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    verifyEmail({ token })
      .then(() => {
        if (cancelled) return;
        setState('verified');
        /* Match the host: send them to sign in a moment later. */
        setTimeout(() => window.location.assign('/login'), 3000);
      })
      .catch((e) => {
        if (cancelled) return;
        setFailMsg(
          e?.status === 401 || e?.status === 400
            ? 'This verification link has expired or has already been used. Request a new one below.'
            : e?.message || 'We couldn’t verify your email just now. Please try again.',
        );
        setState('failed');
      });
    return () => { cancelled = true; };
  }, [token]);

  const resend = () => {
    if (!signedIn) {
      setErr('Sign in first, then use “Resend verification email” — the link is tied to your account.');
      return;
    }
    setBusy(true);
    setErr('');
    resendVerification()
      .then(() => setResent(true))
      .catch((e) => {
        const s = e?.status;
        setErr(
          s === 401
            ? 'Your session expired. Sign in again and we’ll send a fresh link.'
            : s === 400
              ? 'That address is already verified — you can sign in now.'
              : s === 429
                ? 'Too many requests. Wait a minute before asking for another link.'
                : e?.message || 'Something went wrong. Please try again.',
        );
      })
      .finally(() => setBusy(false));
  };

  const ResendBlock = () => (
    <>
      {!resent && (
        <>
          {!signedIn && (
            <p style={{ fontSize: '12.5px', lineHeight: '20px', color: 'var(--ink-soft)', marginTop: '16px' }}>
              Need a new link? <a href="/login" style={{ color: 'var(--teal-deep)', fontWeight: 700 }}>Sign in</a> and
              we&apos;ll send one — verification links are tied to your account, so we can&apos;t resend from here.
            </p>
          )}
          {err && <p className="ax-err" role="alert" style={{ marginTop: '14px' }}><IconWarn />{err}</p>}
          {signedIn && (
            <button className="btn-primary" type="button" onClick={resend} disabled={busy} style={{ width: '100%', marginTop: '18px' }}>
              {busy ? 'Sending…' : 'Resend verification email'}
            </button>
          )}
        </>
      )}
      {resent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#eef8f3', border: '1px solid #cfe8dc', borderRadius: '12px', padding: '14px 16px', marginTop: '20px' }}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
            <path fill="none" stroke="#1d7a43" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" />
          </svg>
          <p style={{ fontSize: '13px', color: 'var(--ink)', margin: 0 }}>A new link is on its way. It can take a minute to land.</p>
        </div>
      )}
    </>
  );

  if (state === 'verifying') {
    return (
      <>
        <Seo title="Verifying your email | TeamGrid™" description="Confirming your TeamGrid email address." noindex />
        <Card>
          <h1>Verifying your email…</h1>
          <p className="asub">One moment while we activate your workspace.</p>
        </Card>
      </>
    );
  }

  if (state === 'verified') {
    return (
      <>
        <Seo title="Email verified | TeamGrid™" description="Your TeamGrid email address is verified and your workspace is active." noindex />
        <Card>
          <div className="oksplash show">
            <div className="okring">
              <svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16"><path fill="none" stroke="#1d7a43" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'var(--ink-strong)' }}>Email verified</h3>
            <p style={{ fontSize: '13.5px', color: 'var(--ink-soft)', marginTop: '8px' }}>
              Your workspace is active. Taking you to sign in…
            </p>
            <a className="btn-primary" href="/login" style={{ marginTop: '18px', width: '100%' }}>Sign in now</a>
          </div>
        </Card>
      </>
    );
  }

  if (state === 'failed') {
    return (
      <>
        <Seo title="Verification link problem | TeamGrid™" description="That TeamGrid verification link didn’t work. Request a new one." noindex />
        <Card>
          <h1>That link didn&apos;t work</h1>
          <p className="asub">{failMsg}</p>
          <ResendBlock />
          <p className="aswitch">Already verified? <a href="/login">Sign in</a></p>
        </Card>
      </>
    );
  }

  return (
    <>
      <Seo title="Verify Your Email | TeamGrid™" description="Click the verification link we emailed you to activate your TeamGrid workspace. Verifying turns on every module, team invites and your pattern reports." noindex />
      <Card>
        <h1>Check your inbox</h1>
        <p className="asub">
          We sent a verification link to <span style={{ color: 'var(--teal-deep)', fontWeight: '700' }}>{known || 'your inbox'}</span>.
          Click it and your workspace is ready.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 22px' }}>
          <span style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#eaf2fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24">
              <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" fill="none" stroke="#366EB6" strokeWidth="2.2" />
              <path d="M4.5 8l7.5 5 7.5-5" stroke="#366EB6" strokeWidth="2.2" fill="none" />
            </svg>
          </span>
        </div>

        <div style={{ background: '#f6fafa', border: '1px solid var(--line)', borderRadius: '12px', padding: '16px 18px' }}>
          <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '.8px', color: 'var(--ink-faint)', marginBottom: '10px' }}>WHAT VERIFYING TURNS ON</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {ACTIVATED.map((item) => (
              <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontSize: '13px', lineHeight: '22px', color: 'var(--ink)' }}>
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" style={{ marginTop: '4px', flexShrink: 0 }}>
                  <path fill="none" stroke="#007A6E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <ResendBlock />

        <p style={{ fontSize: '12.5px', lineHeight: '20px', color: 'var(--ink-soft)', textAlign: 'center', marginTop: '18px' }}>
          Nothing after a few minutes? Check spam. Links expire, so use the newest one.
        </p>

        <div style={{ borderTop: '1px solid var(--line-soft)', marginTop: '18px', paddingTop: '16px' }}>
          <p style={{ fontSize: '12.5px', lineHeight: '20px', color: 'var(--ink-soft)', textAlign: 'center' }}>
            Wrong address? <a href="/register" style={{ color: 'var(--teal-deep)', fontWeight: '700' }}>Sign up again with the right email</a>,
            or ask us to move it: <a href="mailto:support@teamgrid.ai" style={{ color: 'var(--teal-deep)', fontWeight: '700' }}>support@teamgrid.ai</a>
          </p>
        </div>

        <p className="aswitch">Already verified? <a href="/login">Sign in</a></p>
      </Card>
    </>
  );
}
